/**
 * Map backend /csadmin API payloads to UI shapes used by list/detail components.
 */

import {
  normalizeCommitteeTypeCode,
  committeeTypeLabel,
} from '../constants/committeeTypes';
import { normalizeShareholdingPercent } from './shareholdingPercent';
import { computeCompanySetupProgress, setupProgressLabel } from './companySetupProgress';

export function formatDisplayDate(value) {
  if (!value) return '—';
  const date = parseMeetingDate(value) || (value instanceof Date ? value : new Date(value));
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-GB');
}

/** Parse API/Oracle date as local calendar day (avoids UTC midnight shifting the day). */
export function parseMeetingDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value).trim();
  const dateOnly = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
  }
  const parsed = new Date(str);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

export function getMeetingDate(meeting) {
  if (!meeting) return null;
  return parseMeetingDate(meeting.date ?? meeting.meeting_date ?? meeting.meetingDate);
}

export function isMeetingUpcoming(meeting) {
  if (!meeting || meeting.status !== 'SCHEDULED') return false;
  const date = getMeetingDate(meeting);
  if (!date) return false;
  return date >= startOfToday();
}

export function isMeetingPastDue(meeting) {
  if (!meeting || meeting.status !== 'SCHEDULED') return false;
  const date = getMeetingDate(meeting);
  if (!date) return false;
  return date < startOfToday();
}

export function getMeetingDisplayStatus(meeting) {
  if (!meeting) return '';
  if (meeting.status === 'CANCELLED') return 'CANCELLED';
  if (meeting.status === 'COMPLETED') return 'COMPLETED';
  if (isMeetingPastDue(meeting)) return 'PAST DUE';
  return meeting.status || 'SCHEDULED';
}

export function formatCompanyTypeLabel(type) {
  const normalized = String(type || '').trim().toUpperCase();
  if (!normalized) return '—';
  const known = {
    'E&G': 'E&G',
    LS: 'Local Subsidiary',
    FS: 'Foreign Subsidiary',
    ADGM: 'ADGM',
    V: 'Vertical',
    VS: 'Vertical Subsidiary',
    HOLDING: 'Holding',
    PARENT: 'Parent',
    SUBSIDIARY: 'Subsidiary',
  };
  if (known[normalized]) return known[normalized];
  return type;
}

export function mapLegacyGovernanceCode(code) {
  const normalized = String(code || '').trim().toUpperCase();
  if (!normalized) return 'LS';
  if (normalized === 'HOLDING' || normalized === 'PARENT') return 'E&G';
  if (normalized === 'SUBSIDIARY') return 'LS';
  return code;
}

export function inferCompanyStructure(row) {
  if (row?.structureType) return row.structureType;
  const parentId = row?.parent_company_id ?? row?.parentCompanyId;
  return parentId ? 'Subsidiary' : 'Parent';
}

export function parseCompanyDescription(description) {
  if (!description) return { address: '', city: '', poBox: '' };
  const parts = String(description).split(',').map((part) => part.trim());
  return {
    address: parts[0] || '',
    city: parts[1] || '',
    poBox: parts[2] || '',
  };
}

export function mapDirectorFromApi(row) {
  if (!row) return null;
  const id = row.director_id ?? row.id;
  const isActive = row.active_appointment_count != null
    ? Number(row.active_appointment_count) > 0
    : row.is_active === 1 || row.is_active === true || row.status === 'ACTIVE';
  return {
    id,
    director_id: id,
    firstName: row.first_name || '',
    lastName: row.last_name || '',
    full_name: row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim(),
    avatar: row.photo_url || null,
    emiratesId: row.emirates_id || '',
    passportNumber: row.passport_number || '',
    companyName: row.company_name || '',
    companyId: row.company_id,
    nationality: row.nationality || '',
    status: isActive ? 'ACTIVE' : 'INACTIVE',
    position: row.position || '',
    appointment_date: row.appointment_date,
    appointmentDate: row.appointment_date || '',
    email: row.email || '',
    phone: row.phone || '',
    address: row.official_street_address || row.address || '',
    dateOfBirth: row.date_of_birth || '',
    director_category: row.director_category,
    passport_expiry_date: row.passport_expiry_date,
    emirates_id_expiry_date: row.emirates_id_expiry_date,
    passport_file_url: row.passport_file_url,
    emirates_id_file_url: row.emirates_id_file_url,
    photo_url: row.photo_url,
    documents: row.documents || [],
    committees: row.committees || [],
    activities: row.activities || [],
    auditLogs: row.audit_logs || row.auditLogs || [],
    committeeCount: row.committee_count ?? (row.committees || []).length,
    companies: (row.companies || []).map(mapDirectorCompanyFromApi).filter(Boolean),
    companyCount: row.company_count ?? (row.companies || []).length,
  };
}

export function mapDirectorCompanyFromApi(row) {
  if (!row) return null;
  const companyId = row.company_id ?? row.companyId ?? row.id;
  return {
    id: companyId,
    companyId,
    companyName: row.company_name || row.companyName || row.name || '',
    directorId: row.director_id ?? row.directorId,
    position: row.position || 'Director',
    appointmentDate: row.appointment_date || row.appointmentDate || null,
    endDate: row.end_date || row.endDate || null,
    isActive: row.is_active !== false && row.is_active !== 0,
    isCurrentRecord: row.is_current_record === true || row.is_current_record === 1,
    companyType: row.company_type_name || row.company_type_code || row.companyType || '',
  };
}

export function isEtisalatEntityName(name) {
  const n = String(name || '').toLowerCase();
  return n.includes('etisalat') || n.includes('e&') || n.includes('emirates telecom');
}

export function mapShareholderFromApi(row) {
  if (!row) return null;
  const name = row.name || row.shareholderName || '';
  const isFlag = row.is_etisalat_entity === true
    || row.is_etisalat_entity === 1
    || row.isEtisalatEntity === true;
  return {
    id: row.id ?? row.shareholder_id,
    shareholderName: name,
    shareholderType: String(row.type || row.shareholder_type || 'CORPORATE').toUpperCase(),
    percentage: normalizeShareholdingPercent(row.percentage) ?? 0,
    nationality: row.nationality || '',
    notes: row.notes || '',
    isEtisalatEntity: isFlag || isEtisalatEntityName(name),
  };
}

export function mapDirectorToEditForm(row) {
  const mapped = mapDirectorFromApi(row);
  if (!mapped) return null;
  const parseDate = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };
  return {
    companyId: mapped.companyId || '',
    companyName: mapped.companyName || '',
    firstName: mapped.firstName || '',
    lastName: mapped.lastName || '',
    emiratesId: mapped.emiratesId || '',
    passportNumber: mapped.passportNumber || '',
    nationality: mapped.nationality || '',
    dateOfBirth: parseDate(mapped.dateOfBirth),
    gender: '',
    email: mapped.email || '',
    mobileNumber: mapped.phone || '',
    address: mapped.address || '',
    position: mapped.position || '',
    appointmentDate: parseDate(mapped.appointmentDate || mapped.appointment_date),
    passportExpiryDate: parseDate(mapped.passport_expiry_date),
    emiratesIdExpiryDate: parseDate(mapped.emirates_id_expiry_date),
    passportFile: null,
    emiratesIdFile: null,
  };
}

/** Map a director list row for pool/search UI (Add Director, SearchPoolDialog). */
export function mapDirectorPoolEntryFromApi(row, targetCompanyId) {
  if (!row) return null;

  // Person registry pool row (deduped by person_id)
  if (row.person_id != null || row.personId != null) {
    const personId = row.person_id ?? row.personId;
    const repDirectorId = row.representative_director_id ?? row.directorId ?? row.id;
    const atTarget = row.already_at_target_company === true
      || row.isAssigned === true
      || Number(row.at_target_company) > 0;
    const companyCount = Number(row.company_count ?? row.companyCount) || 0;
    return {
      id: repDirectorId,
      directorId: repDirectorId,
      personId,
      person_id: personId,
      fullName: row.fullName || row.full_name || '—',
      emiratesId: row.emiratesId || row.emirates_id || '—',
      phone: row.email || row.primary_email || '',
      position: row.position || 'Director',
      currentCompany: row.currentCompany || row.companyName || row.sample_company_name || '—',
      companyCount,
      isEligible: row.isEligible !== false && !atTarget,
      isAssigned: atTarget,
      ineligibilityReason: atTarget
        ? (row.ineligibilityReason || 'Already assigned to the selected company')
        : null,
    };
  }

  const mapped = mapDirectorFromApi(row);
  if (!mapped) return null;
  const fullName = mapped.full_name || `${mapped.firstName} ${mapped.lastName}`.trim();
  const atTarget = row.already_at_target_company === true
    || row.already_at_target_company === 1
    || Number(row.at_target_company) > 0;
  const assignedToTarget = atTarget || (
    targetCompanyId
    && mapped.companyId != null
    && String(mapped.companyId) === String(targetCompanyId)
  );
  const companyCount = Number(row.company_count ?? mapped.company_count) || (mapped.companyId ? 1 : 0);
  return {
    id: mapped.id,
    directorId: mapped.id,
    userId: row.user_id ?? row.userId,
    fullName,
    emiratesId: mapped.emiratesId || '—',
    phone: mapped.email || '',
    position: mapped.position || 'Director',
    currentCompany: mapped.companyName || '—',
    companyCount,
    isEligible: !assignedToTarget,
    isAssigned: assignedToTarget,
    ineligibilityReason: assignedToTarget
      ? 'Already assigned to the selected company'
      : null,
  };
}

export function mapCompanyFromApi(row) {
  if (!row) return null;
  const id = row.company_id ?? row.id;
  const governanceCode = mapLegacyGovernanceCode(row.company_type_code);
  const governanceName = row.company_type_name || formatCompanyTypeLabel(governanceCode);
  const structureType = inferCompanyStructure(row);
  const base = {
    id,
    company_id: id,
    name: row.company_name || row.name,
    company_name: row.company_name || row.name,
    tradeLicenseNumber: row.registration_number || row.trade_license_number,
    registration_number: row.registration_number,
    country: row.country,
    parent_company_id: row.parent_company_id,
    parent_company_name: row.parent_company_name,
    parentCompany: row.parent_company_name,
    is_active: row.is_active,
    status: row.is_active === 1 || row.is_active === true ? 'ACTIVE' : 'INACTIVE',
    structureType,
    governanceTypeCode: governanceCode,
    governanceTypeName: governanceName,
    type: governanceName,
    company_type_code: governanceCode,
    company_type_name: governanceName,
    shareholding_percentage: normalizeShareholdingPercent(row.shareholding_percentage),
    shareholdingPercentage: normalizeShareholdingPercent(row.shareholding_percentage),
    description: row.description || '',
    phone: row.phone || '',
    email: row.email || '',
    website: row.website || '',
    revenue: row.revenue || '',
    capital: row.capital || row.revenue || '',
    foundedYear: row.founded_year ?? row.foundedYear ?? null,
    founded_year: row.founded_year ?? row.foundedYear ?? null,
    employeeCount: row.employee_count ?? row.employeeCount ?? null,
    employee_count: row.employee_count ?? row.employeeCount ?? null,
    ceoName: row.ceo_name || row.ceoName || '',
    ceo_name: row.ceo_name || row.ceoName || '',
    entityClassification: row.entity_classification || row.entityClassification || '',
    entity_classification: row.entity_classification || row.entityClassification || '',
    profileSummary: row.profile_summary || row.profileSummary || '',
    profile_summary: row.profile_summary || row.profileSummary || '',
    created_at: row.created_at,
    createdDate: row.created_at || row.createdDate,
    director_count: row.director_count,
    document_count: row.document_count,
    officer_count: row.officer_count,
    committee_count: row.committee_count,
    assignedCSId: row.assignedCSId ?? row.assigned_cs_id ?? null,
    assignedCS: row.assignedCS || row.assigned_cs || null,
    assignedCSEmail: row.assignedCSEmail || row.assigned_cs_email || null,
  };
  const setupProgress = computeCompanySetupProgress(base);
  return {
    ...base,
    setupProgress,
    setupStatus: setupProgressLabel(setupProgress),
  };
}

export function mapExpiryAlertFromApi(row) {
  if (!row) return null;
  const expiry = row.expiry_date ? new Date(row.expiry_date) : null;
  const daysUntilExpiry = expiry
    ? Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))
    : null;
  const docLabel =
    {
      PASSPORT: 'Passport',
      EMIRATES_ID: 'Emirates ID',
      VISA: 'Visa',
      WORK_PERMIT: 'Work Permit',
      TRADE_LICENSE: 'Trade License',
      BOARD_RESOLUTION: 'Board Resolution',
      POWER_OF_ATTORNEY: 'Power of Attorney',
      COMMERCIAL_REGISTRATION: 'Commercial Registration',
      OTHER: 'Other',
      DOCUMENT: 'Document',
    }[row.document_type] || row.document_name || row.document_type || 'Document';

  return {
    id: row.id,
    documentType: docLabel,
    document_type: row.document_type,
    directorName: row.director_name,
    companyName: row.company_name,
    documentNumber: row.document_number || row.document_name,
    documentName: row.document_name,
    expiryDate: row.expiry_date,
    daysUntilExpiry,
    status: row.status,
    director_id: row.director_id,
    person_id: row.person_id,
    company_id: row.company_id,
    document_id: row.document_id,
    source: row.source,
  };
}

export function mapDashboardFromApi(apiData, { companies = [], isOverview = true, companyName = '' } = {}) {
  const d = apiData || {};
  const inactive =
    (d.directors?.total ?? 0) - (d.directors?.active ?? 0);

  return {
    totalDirectors: d.directors?.total ?? 0,
    activeDirectors: d.directors?.active ?? 0,
    inactiveDirectors: inactive > 0 ? inactive : 0,
    totalCommittees: d.committees?.total ?? 0,
    activeCommittees: d.committees?.active ?? d.committees?.total ?? 0,
    totalCompanies: d.companies?.total ?? companies.length,
    activeCompanies: d.companies?.active ?? companies.length,
    pendingCompanies: 0,
    totalCS: d.company_secretaries?.total ?? 0,
    activeCS: d.company_secretaries?.active ?? 0,
    companyName: isOverview ? '' : companyName,
    expiryAlerts: {
      overdue: 0,
      urgent: 0,
      warning: 0,
      total: d.expiry_alerts ?? 0,
    },
    recentActivity: d.recent_activity || [],
    upcomingMeetings: d.upcoming_meetings || [],
  };
}

export function mapCommitteeFromApi(row) {
  if (!row) return null;
  const rawType = row.type ?? row.committee_type;
  const typeCode = normalizeCommitteeTypeCode(rawType);
  return {
    id: row.id ?? row.committee_id,
    name: row.name ?? row.committee_name,
    type: committeeTypeLabel(rawType),
    typeCode,
    committee_type: typeCode || rawType,
    memberCount: row.memberCount ?? row.member_count ?? 0,
    meetingCount: row.meetingCount ?? row.meeting_count ?? (row.meetings?.length ?? 0),
    quorum: row.quorum,
    lastMeeting: row.lastMeeting ?? row.last_meeting,
    nextMeeting: row.nextMeeting ?? row.next_meeting,
    status: row.status || 'ACTIVE',
    chairperson: row.chairperson ?? row.chairperson_name,
    company: row.company ?? row.company_name,
    companyId: row.companyId ?? row.company_id,
    members: row.members || [],
    meetings: row.meetings || [],
    documents: row.documents || [],
    activity: row.activity || [],
    created_at: row.created_at ?? row.createdDate,
    createdDate: row.createdDate ?? row.created_at,
  };
}

function mapAssignedCompanyEntry(entry, index, assignedIds = []) {
  if (typeof entry === 'string') {
    return {
      id: assignedIds[index],
      name: entry,
      type: 'Parent',
      role: 'Primary CS',
    };
  }
  const parentCompanyId = entry.parentCompanyId ?? entry.parent_company_id;
  return {
    id: entry.id ?? entry.company_id ?? assignedIds[index],
    name: entry.name ?? entry.company_name ?? '',
    type: entry.type || (parentCompanyId ? 'Subsidiary' : 'Parent'),
    assignedDate: entry.assignedDate ?? entry.assigned_date ?? null,
    role: entry.role || 'Primary CS',
  };
}

function hasAssignedCompanyObjects(assignedCompanies) {
  return Array.isArray(assignedCompanies)
    && assignedCompanies.some((entry) => entry && typeof entry === 'object');
}

/** Normalize secretary company assignments for detail views and modals. */
export function normalizeSecretaryAssignedCompanies(data) {
  if (!data) return [];

  const assignedIds = data.assignedCompanyIds || [];
  const rawAssigned = data.assignedCompanies;

  if (hasAssignedCompanyObjects(rawAssigned)) {
    return rawAssigned.map((entry, index) => mapAssignedCompanyEntry(entry, index, assignedIds));
  }

  const names = Array.isArray(data.companyNames) && data.companyNames.length
    ? data.companyNames
    : Array.isArray(data.companies)
      ? data.companies
      : [];

  return names.map((entry, index) => mapAssignedCompanyEntry(entry, index, assignedIds));
}

export function mapCompanySecretaryFromApi(row) {
  if (!row) return null;
  const name = row.name || `${row.firstName || row.first_name || ''} ${row.lastName || row.last_name || ''}`.trim();
  const assignedIds = row.assignedCompanyIds || [];

  const companyNames = Array.isArray(row.companyNames)
    ? row.companyNames
    : Array.isArray(row.companies) && row.companies.every((entry) => typeof entry === 'string' || entry?.name)
      ? row.companies.map((entry) => (typeof entry === 'string' ? entry : entry.name)).filter(Boolean)
      : hasAssignedCompanyObjects(row.assignedCompanies)
        ? row.assignedCompanies.map((entry) => entry.name ?? entry.company_name).filter(Boolean)
        : [];

  const assignedCompanyDetails = hasAssignedCompanyObjects(row.assignedCompanies)
    ? row.assignedCompanies.map((entry, index) => mapAssignedCompanyEntry(entry, index, assignedIds))
    : [];

  const assignedCount = assignedCompanyDetails.length
    || (typeof row.assignedCompanies === 'number' ? row.assignedCompanies : companyNames.length);

  return {
    id: row.id,
    name,
    firstName: row.firstName ?? row.first_name ?? name.split(' ')[0] ?? '',
    lastName: row.lastName ?? row.last_name ?? name.split(' ').slice(1).join(' ') ?? '',
    username: row.username || '',
    email: row.email,
    phone: row.phone,
    status: row.status,
    // List API: number count. Detail API: array of { id, name, type, ... }.
    assignedCompanies: assignedCompanyDetails.length ? assignedCompanyDetails : assignedCount,
    assignedCompanyCount: assignedCount,
    assignedCompanyIds: assignedIds,
    companyNames,
    companies: companyNames,
    createdDate: row.createdDate ?? row.created_at,
    lastLogin: row.lastLogin ?? row.last_login,
    statistics: row.statistics,
    role: row.role || row.portal_role || row.roleLabel || 'COMPANY_SECRETARY',
    roleLabel: row.roleLabel,
  };
}

export function mergeExpirySummaryIntoDashboard(stats, summary) {
  if (!summary) return stats;
  return {
    ...stats,
    expiryAlerts: {
      overdue: summary.overdue ?? 0,
      urgent: summary.urgent ?? 0,
      warning: summary.warning ?? 0,
      valid: summary.valid ?? 0,
      total:
        (summary.overdue ?? 0) +
        (summary.urgent ?? 0) +
        (summary.warning ?? 0),
    },
  };
}
